import classnames from 'classnames'
import React, { forwardRef } from 'react'

import LAYOUT_TYPES from '@src/quince-site-common/src/constants/layoutTypes'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { HARD_CODED_IMAGE_QUALITY } from '@src/quince-site-common/src/utils/constants'
import getMaxWidthInPx from '@src/quince-site-common/src/utils/getMaxWidthInPx'

import IconWithTextfeature, { IconWithTextfeatureProps } from '../iconWithTextfeature/IconWithTextfeature'
import { Image, ImageProps } from '../image'
import Video, { VideoProps, VIDEO_LOADING_TYPE, VIDEO_PRELOAD_TYPE } from '../video'

import styles from './featureSectionTwo.module.scss'

export interface FeatureSectionTwoProps {
  heading: string
  layoutType?: LAYOUT_TYPES
  description?: string
  imageProps: ImageProps
  videoProps?: VideoProps
  contents: IconWithTextfeatureProps[]
  className?: string
}

const renderMedia = (
  imageBoxClassnames: string,
  imageProps: ImageProps,
  videoProps: VideoProps | undefined,
  deviceType: DEVICE_TYPES,
) => {
  if (videoProps?.url) {
    return (
      <div className={imageBoxClassnames}>
        <Video
          {...videoProps}
          isPresentation
          muted
          autoplay
          loop
          controls={false}
          loadingType={VIDEO_LOADING_TYPE.Lazy}
          preload={VIDEO_PRELOAD_TYPE.Auto}
          className={classnames(videoProps.className)}
        />
      </div>
    )
  }

  return (
    <div className={imageBoxClassnames}>
      <Image
        {...imageProps}
        alt={imageProps.alt || imageProps.title}
        quality={HARD_CODED_IMAGE_QUALITY}
        maxWidth={getMaxWidthInPx({
          maxWidthDesktopInRem: styles.leftBoxImageWidthDesktop || 0,
          maxWidthMobileInRem: styles.leftBoxImageWidthMobile || 0,
          deviceType,
        })}
      />
    </div>
  )
}

const renderSubSections = (
  subSectionsBoxClassName: string,
  subSectionsBoxInnerClassName: string,
  contents: IconWithTextfeatureProps[],
) => (
  <div className={subSectionsBoxClassName}>
    <div className={subSectionsBoxInnerClassName}>
      {contents.map((item: IconWithTextfeatureProps, index: number) => {
        return (
          <IconWithTextfeature
            {...item}
            align="left"
            className={classnames(styles.infoItem, styles.Icon, styles[`infoItem__item_${index}`])}
            key={index}
            imageWrapperClassName={classnames(
              styles.infoItem__imageWrapperClassName,
              styles[`infoItem__imageWrapperClassName__${index + 1}`],
            )}
          />
        )
      })}
    </div>
  </div>
)

export const FeatureSectionTwo = forwardRef<HTMLDivElement, FeatureSectionTwoProps>(
  (props: FeatureSectionTwoProps, ref) => {
    const wrapClassnames = classnames(styles.featureSectiontwoWrap, props?.className)
    const subSectionsBoxClassName = classnames(styles.subSectionsBox, styles.infoBox)
    const subSectionsBoxInnerClassName = styles.subSectionsBoxInner || ''
    const imageBoxClassnames = styles.imageBox || ''

    const deviceInfo = useDeviceInfo()

    return (
      <div className={wrapClassnames} ref={ref}>
        {renderMedia(imageBoxClassnames, props.imageProps, props.videoProps, deviceInfo.deviceType)}
        {renderSubSections(subSectionsBoxClassName, subSectionsBoxInnerClassName, props.contents)}
      </div>
    )
  },
)

FeatureSectionTwo.displayName = 'FeatureSectionTwo'

export default FeatureSectionTwo
